/**
 * ConfirmDialogView — pure presentation for the shared
 * confirm/destructive dialog molecule (SE-5524).
 *
 * Single AlertDialog chrome for every "are you sure?" flow in the app:
 * optional icon in a tone circle, centered title, stacked body lines, an
 * optional type-to-confirm input, an optional required-reason field with a
 * live character counter (SE-5500), an optional error row, and a standardized
 * Cancel/confirm footer.
 * All styling is closed-token; the tone prop selects from fixed
 * circle/icon/button pairings so no caller can inject a className.
 *
 * GlueStack's AlertDialog primitive does not set the ARIA role on web —
 * set it explicitly so screen readers announce an alert dialog requiring
 * user attention rather than a generic container (SE-4592).
 * @module components/molecules/ConfirmDialog/ConfirmDialogView
 */

import { X } from "lucide-react-native";
import React, { memo } from "react";
import type { ReactNode } from "react";

import {
  AlertDialog,
  AlertDialogBackdrop,
  AlertDialogBody,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
} from "@/components/atoms/AlertDialog";
import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import type { ButtonTone } from "@/components/atoms/Button";
import { Icon } from "@/components/atoms/Icon";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import { TextInput } from "@/components/atoms/TextInput";
import { Textarea, TextareaInput } from "@/components/atoms/Textarea";

/** Semantic intent of the dialog — drives the icon circle + confirm button. */
export type ConfirmDialogTone = "danger" | "warning" | "primary";

/**
 * Which shape the dialog takes.
 *
 * `alert` is the house style every existing caller gets: a tone circle over a
 * centred title, a close X, and a footer flush under the body. It suits a
 * one-sentence "are you sure?".
 *
 * `document` is the shape Figma draws for a confirmation the reader has to
 * READ — a left-aligned title, no icon, no close X, and a rule separating the
 * actions from the content. It exists because the capture screen's submit
 * confirmation carries a list of players and a warning panel, and centring a
 * title over a left-aligned table looks like a mistake.
 *
 * **It also carries the accent confirm button, and that is one spec rather than
 * two choices.** The frame draws that button `#007aff`, which is
 * `accent-primary`. The `alert` style keeps `TONE_TO_CONFIRM_BUTTON_TONE`
 * untouched, so no existing dialog changes colour — see the note on that map
 * about the drift this leaves standing.
 */
export type ConfirmDialogPresentation = "alert" | "document";

/**
 * Closed tone → icon-circle classes. Full literal strings (no template
 * composition) so the tailwind lint can verify every token.
 */
const TONE_TO_CIRCLE_CLASS: Record<ConfirmDialogTone, string> = {
  danger:
    "items-center justify-center rounded-full bg-status-error-surface p-3",
  warning:
    "items-center justify-center rounded-full bg-status-warning-surface p-3",
  primary:
    "items-center justify-center rounded-full bg-status-info-surface p-3",
};

/** Closed tone → icon color class. */
const TONE_TO_ICON_CLASS: Record<ConfirmDialogTone, string> = {
  danger: "text-status-error",
  warning: "text-status-warning",
  primary: "text-status-info",
};

/**
 * Closed tone → confirm Button tone. `warning` pairs with a primary confirm:
 * warning dialogs guard recoverable/cautionary actions (e.g. "Mark as
 * Departed"), so the confirm is affirmative rather than destructive-red.
 *
 * **`primary` here is Gluestack's near-black, not the product's blue, and that
 * is unresolved drift.** The Button atom's own docs say "the Figma confirm
 * button is `accent-primary` (SE-6233)", and the capture confirmation's frame
 * draws `#007aff` — so this map disagrees with the design system on every
 * `tone="primary"` dialog in the app. It is left alone deliberately: correcting
 * it would recolour ~24 call sites at once, which is a reviewed change rather
 * than a side effect of one screen's fidelity pass. `presentation="document"`
 * gets the accent button without touching any of them.
 */
const TONE_TO_CONFIRM_BUTTON_TONE: Record<ConfirmDialogTone, ButtonTone> = {
  danger: "danger",
  warning: "primary",
  primary: "primary",
};

/** Props for {@link ConfirmDialogView} — fully resolved by the Container. */
export interface ConfirmDialogViewProps {
  /** Whether the dialog is visible. */
  readonly isOpen: boolean;
  /** Cancel / backdrop / close-X handler. */
  readonly onClose: () => void;
  /** Confirm handler (already gated by the Container). */
  readonly onConfirm: () => void;
  /** Dialog title. */
  readonly title: string;
  /** Stable element id for aria-labelledby. */
  readonly titleId: string;
  /** Pre-formatted body lines (first line leads, later lines are muted). */
  readonly bodyLines: readonly string[];
  /**
   * Extra body content, rendered under the lines.
   *
   * A slot rather than more props, because what goes here is whatever a
   * particular confirmation has to SHOW — a grouped summary, a warning panel —
   * and encoding each of those as a prop on the shared dialog would grow this
   * component once per caller. The chrome, focus handling and footer stay
   * single-sourced, which is the part worth sharing.
   *
   * Callers own the layout inside the slot. They do NOT own the dialog.
   */
  readonly bodySlot?: ReactNode;
  /** Optional Lucide icon rendered in the tone circle. */
  readonly icon?: React.ElementType;
  /** Semantic tone pairing. */
  readonly tone: ConfirmDialogTone;
  /** Which shape the dialog takes; defaults to the house `alert` style. */
  /**
   * Whether the dialog takes its document shape, resolved by
   * `ConfirmDialogContainer` (SE-8019). The View renders the
   * shape; it does not decide it.
   */
  readonly isDocument: boolean;
  /** Cancel button label. */
  readonly cancelLabel: string;
  /** Confirm button label. */
  readonly confirmLabel: string;
  /** Whether the confirm action is in flight. */
  readonly isConfirming: boolean;
  /** Whether the confirm button is disabled (caller flag OR typed gate). */
  readonly isConfirmDisabled: boolean;
  /**
   * Typed-confirmation input state; undefined hides the input.
   *
   * The field is UNCONTROLLED on purpose (SE-6888). `defaultValue` is read
   * once when the field mounts and never written again, so the text the user
   * typed only ever flows OUT through `onChangeText` — there is no JS→native
   * value write that can land late and overwrite in-flight keystrokes. Do not
   * reintroduce a `value` prop here: that is precisely the defect, and the
   * only thing that ever needed the text is the container's gate comparison,
   * which `onChangeText` already feeds.
   */
  readonly typedConfirmation?: {
    readonly defaultValue: string;
    readonly placeholder: string;
    readonly onChangeText: (text: string) => void;
  };
  /**
   * SE-5500 required-reason field state; undefined hides the field. The live
   * `n/max` counter is what surfaces the cap to the user BEFORE they can write
   * past it — the field stops accepting input at `maxLength` rather than
   * letting an over-long reason reach a server that rejects it outright.
   */
  readonly reasonField?: {
    readonly value: string;
    readonly placeholder: string;
    readonly accessibilityLabel: string;
    readonly maxLength: number;
    readonly helperText?: string;
    readonly onChangeText: (text: string) => void;
  };
  /** Error copy rendered under the fields; undefined hides the row. */
  readonly errorMessage?: string;
  /** Render the dialog inside a native RN Modal (overlay stacking). */
  readonly useRNModal?: boolean;
  /** Dialog width preset. */
  readonly size: "sm" | "md";
  /** Root testID (AlertDialogContent). */
  readonly testID?: string;
  /** Icon circle testID. */
  readonly iconTestID?: string;
  /** Cancel button testID. */
  readonly cancelTestID?: string;
  /** Confirm button testID. */
  readonly confirmTestID?: string;
  /** Typed-confirmation input testID. */
  readonly inputTestID?: string;
  /** Required-reason field testID. */
  readonly reasonTestID?: string;
  /** Error row testID. */
  readonly errorTestID?: string;
}

/**
 * Renders the shared confirm/destructive alert dialog.
 * @param props - View props (see {@link ConfirmDialogViewProps})
 * @param props.isOpen - Whether the dialog is visible
 * @param props.onClose - Cancel / backdrop / close-X handler
 * @param props.onConfirm - Confirm handler (already gated by the Container)
 * @param props.title - Dialog title
 * @param props.titleId - Stable element id for aria-labelledby
 * @param props.bodyLines - Pre-formatted body lines
 * @param props.bodySlot - Extra body content rendered under the lines
 * @param props.icon - Optional Lucide icon rendered in the tone circle
 * @param props.tone - Semantic tone pairing
 * @param props.isDocument - Container-resolved document-shape flag
 * @param props.cancelLabel - Cancel button label
 * @param props.confirmLabel - Confirm button label
 * @param props.isConfirming - Whether the confirm action is in flight
 * @param props.isConfirmDisabled - Whether the confirm button is disabled
 * @param props.typedConfirmation - Typed-confirmation input state
 * @param props.reasonField - Required-reason field state (SE-5500)
 * @param props.errorMessage - Error copy rendered under the fields
 * @param props.useRNModal - Render inside a native RN Modal for stacking
 * @param props.size - Dialog width preset
 * @param props.testID - Root testID (AlertDialogContent)
 * @param props.iconTestID - Icon circle testID
 * @param props.cancelTestID - Cancel button testID
 * @param props.confirmTestID - Confirm button testID
 * @param props.inputTestID - Typed-confirmation input testID
 * @param props.reasonTestID - Required-reason field testID
 * @param props.errorTestID - Error row testID
 * @returns The rendered alert dialog.
 */
const ConfirmDialogView = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  titleId,
  bodyLines,
  bodySlot,
  icon,
  tone,
  isDocument,
  cancelLabel,
  confirmLabel,
  isConfirming,
  isConfirmDisabled,
  typedConfirmation,
  reasonField,
  errorMessage,
  useRNModal,
  size,
  testID,
  iconTestID,
  cancelTestID,
  confirmTestID,
  inputTestID,
  reasonTestID,
  errorTestID,
}: ConfirmDialogViewProps) => (
  // Bounded to the viewport (SE-8491). The vendored content has a width per
  // size and no height at all, and its body is a ScrollView that never shrinks
  // — so a confirmation listing 22 players grew past the screen, clipping the
  // title above and the actions below, and the only way to submit was to zoom
  // the browser out.
  //
  // Three parts, each inert on a short dialog:
  //   - the root is the viewport (the overlay is absolute-filled), and `py-10`
  //     is the inset that keeps backdrop visible above and below a tall one
  //     — 40 px each side is ~90 vh on a 768 px laptop, which is the feel the
  //     ticket asked for, without an arbitrary value;
  //   - `max-h-full` caps the content at that inset box;
  //   - `min-h-0 shrink` on the body lets the ScrollView give way inside the
  //     cap, while the header and footer — its siblings — keep their own
  //     height. Only the list scrolls; the title and the actions stay put.
  <AlertDialog
    isOpen={isOpen}
    onClose={onClose}
    size={size}
    useRNModal={useRNModal}
    className="py-10"
  >
    <AlertDialogBackdrop />
    <AlertDialogContent
      role="alertdialog"
      aria-modal
      aria-labelledby={titleId}
      testID={testID}
      className="max-h-full"
    >
      <AlertDialogHeader>
        <Stack
          className={
            // Centred on the full dialog width: the close X is pinned to the
            // corner (absolute, below), so the icon and title line up with the
            // body and the actions instead of sitting 16px left of them. Only a
            // title that shares the X's row (no icon above it) needs px-8, the
            // X's own width, on both sides to stay clear of it.
            isDocument
              ? "w-full items-start gap-4 py-2"
              : icon
                ? "w-full items-center gap-4 py-2"
                : "w-full items-center gap-4 px-8 py-2"
          }
        >
          {icon ? (
            <Box className={TONE_TO_CIRCLE_CLASS[tone]} testID={iconTestID}>
              <Icon as={icon} size="lg" className={TONE_TO_ICON_CLASS[tone]} />
            </Box>
          ) : null}
          <Text
            id={titleId}
            variant="title"
            className={
              isDocument
                ? "text-left text-content-primary"
                : "text-center text-content-primary"
            }
          >
            {title}
          </Text>
        </Stack>
        {/* No close X in the document shape: the frame draws none, and the
              footer's Keep editing is the dismissal. A second, unlabelled way
              out beside a labelled one is not more escapable, only more to
              read. Backdrop press and Escape still close it. */}
        {isDocument ? null : (
          <AlertDialogCloseButton className="absolute right-0 top-0">
            <Icon as={X} className="text-content-secondary" />
          </AlertDialogCloseButton>
        )}
      </AlertDialogHeader>

      <AlertDialogBody className="min-h-0 shrink">
        <Stack className="w-full gap-2">
          {bodyLines.map((line, index) => (
            <Text
              key={line}
              variant={index === 0 ? "title-sm" : "body"}
              className={
                index === 0
                  ? "text-center text-content-secondary"
                  : "text-center text-content-muted"
              }
            >
              {line}
            </Text>
          ))}
          {bodySlot}
          {typedConfirmation ? (
            <TextInput
              testID={inputTestID}
              className="w-full rounded-sm border border-outline-default px-3 py-2 text-body text-content-primary"
              defaultValue={typedConfirmation.defaultValue}
              onChangeText={typedConfirmation.onChangeText}
              placeholder={typedConfirmation.placeholder}
              autoCapitalize="none"
              autoCorrect={false}
              editable={!isConfirming}
            />
          ) : null}
          {reasonField ? (
            <Stack className="w-full gap-1">
              <Textarea size="md" className="min-h-24 w-full">
                <TextareaInput
                  testID={reasonTestID}
                  accessibilityLabel={reasonField.accessibilityLabel}
                  value={reasonField.value}
                  onChangeText={reasonField.onChangeText}
                  placeholder={reasonField.placeholder}
                  maxLength={reasonField.maxLength}
                  editable={!isConfirming}
                />
              </Textarea>
              <Stack
                direction="horizontal"
                className="w-full items-center justify-between gap-2"
              >
                <Text variant="caption" className="text-content-muted">
                  {reasonField.helperText ?? ""}
                </Text>
                <Text variant="caption" className="text-content-muted">
                  {`${reasonField.value.length}/${reasonField.maxLength}`}
                </Text>
              </Stack>
            </Stack>
          ) : null}
          {errorMessage ? (
            <Text
              testID={errorTestID}
              variant="body"
              className="text-center text-status-error"
            >
              {errorMessage}
            </Text>
          ) : null}
        </Stack>
      </AlertDialogBody>

      <AlertDialogFooter>
        {/* The vendored footer carries no top spacing, so the actions sat
              flush against the body — most visible with the type-to-confirm
              input, where Delete touched the field (SE-6233). */}
        {/* The document shape rules the actions off from the content, which
              the frame draws. Inset by the dialog's own padding rather than
              full-bleed: edge-to-edge would need a negative margin, and an
              arbitrary value is a worse trade than a rule that stops where the
              text does. */}
        <Stack
          direction="horizontal"
          className={
            isDocument
              ? "w-full justify-end gap-2 border-t border-outline-subtle pt-4"
              : "w-full justify-end gap-2 pt-4"
          }
        >
          <Button
            testID={cancelTestID}
            variant="outline"
            tone="secondary"
            labelTone="primary"
            onPress={onClose}
            isDisabled={isConfirming}
            label={cancelLabel}
          />
          <Button
            testID={confirmTestID}
            tone={isDocument ? "accent" : TONE_TO_CONFIRM_BUTTON_TONE[tone]}
            onPress={onConfirm}
            isLoading={isConfirming}
            isDisabled={isConfirmDisabled}
            label={confirmLabel}
          />
        </Stack>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

ConfirmDialogView.displayName = "ConfirmDialogView";

export default memo(ConfirmDialogView);
